import { Link } from "react-router-dom";
import { MapPin, Clock, Car, ArrowUpRight } from "lucide-react";

const TRIPS = [
  {
    slug: "litchfield",
    title: "Litchfield National Park",
    days: "1–2 days",
    from: "1.5 hrs from Darwin",
    vehicle: "Either — sealed loop road",
    hero: "/trips/litchfield.jpg",
    intro: "The Top End's easiest big day out. Waterfalls, plunge pools and termite mounds on a sealed loop you can do in a day — or camp and swim at dawn.",
    stops: [
      ["Magnetic termite mounds", "First stop on the loop. Two-metre mounds aligned north–south — 10 minutes, great photos."],
      ["Buley Rockhole", "Chain of spa-like pools. Arrive before 10am in the dry season for a quiet swim."],
      ["Florence Falls", "Twin falls into a deep pool — 135 steps down, or the 1 km shady creek walk back."],
      ["Tolmer Falls", "Lookout only (no swimming) — best in late afternoon light."],
      ["Wangi Falls", "Big, easy swim with kiosk and lawns. Closed when water is high — check NT Parks alerts."],
    ],
    tips: "Free entry (Parks Pass not required). Fuel up in Batchelor. Swimming areas close during the wet — check nt.gov.au/parks before you leave.",
  },
  {
    slug: "kakadu",
    title: "Kakadu National Park",
    days: "3–4 days",
    from: "3 hrs to Jabiru",
    vehicle: "Hilux for Nourlangie & Ubirr gravel access · Kluger stays on sealed Arnhem/Kakadu Hwy",
    hero: "/trips/kakadu.jpg",
    intro: "World Heritage rock art, wetlands and escarpment country. Our vehicles are permitted on Kakadu's sealed roads and short graded access tracks — not Jim Jim / Twin Falls 4WD tracks.",
    stops: [
      ["Day 1 — Mary River & Yellow Water", "Drive Arnhem Hwy, stop at Window on the Wetlands. Book the Yellow Water sunset cruise at Cooinda."],
      ["Day 2 — Nourlangie (Burrungkuy)", "1.5 km rock-art loop, then Nawurlandja lookout for sunset over Anbangbang billabong."],
      ["Day 3 — Ubirr & Cahills Crossing", "Ubirr rock art and the classic Nadab floodplain sunset. Watch crocs feed at Cahills Crossing on the tide."],
      ["Day 4 — Bowali & home", "Bowali Visitor Centre, Mamukala wetlands bird hide, back to Darwin by afternoon."],
    ],
    tips: "Kakadu Park Pass required (buy online). Fuel at Jabiru and Cooinda. Hilux only for gravel access roads; no water crossings above door sill under our terms.",
  },
  {
    slug: "adelaide-river",
    title: "Adelaide River & Berry Springs",
    days: "Half–1 day",
    from: "45 min – 1.5 hrs",
    vehicle: "Either — all sealed",
    hero: "/trips/adelaide-river.jpg",
    intro: "The short-hop loop south of Darwin: jumping crocodiles, a natural spring swim and a war-history town — perfect for a first day with the car.",
    stops: [
      ["Jumping Crocodile cruise", "Adelaide River bridge, Arnhem Hwy. Hourly departures, 1 hr — the classic Top End experience."],
      ["Fogg Dam", "Walk the wall at dawn or dusk for thousands of waterbirds. Free."],
      ["Berry Springs Nature Park", "Crystal-clear thermal spring pools under pandanus. BBQs and lawns — bring lunch."],
      ["Adelaide River township", "Adelaide River War Cemetery and the famous pub (home of Charlie the buffalo from Crocodile Dundee)."],
      ["Territory Wildlife Park", "Optional — 2–3 hrs, near Berry Springs. Great with kids."],
    ],
    tips: "Ideal Kluger day. Litchfield is another 40 min past Berry Springs if you want to combine.",
  },
];

export default function Trips() {
  return (
    <div>
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-10">
          <div className="overline text-[#BA5A3A]">Where to go</div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mt-3 max-w-4xl">Top End itineraries<br/><span className="text-[#BA5A3A]">from our Darwin base.</span></h1>
          <p className="mt-6 text-lg text-[#595959] max-w-2xl">Three road trips we recommend to every renter — all within our permitted driving zones. Pick one, or string them together over a week with unlimited kilometres.</p>
          <nav className="mt-8 flex flex-wrap gap-2" data-testid="trip-nav">
            {TRIPS.map(t => <a key={t.slug} href={`#${t.slug}`} className="btn-ghost text-xs">{t.title}</a>)}
          </nav>
        </div>
      </section>

      {TRIPS.map((t, idx) => (
        <section key={t.slug} id={t.slug} className={`border-b border-black/10 ${idx % 2 ? "bg-white/40" : ""}`} data-testid={`trip-${t.slug}`}>
          <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <img src={t.hero} alt={t.title} className="w-full h-72 object-cover border border-black/10" />
              <div className="mt-6 space-y-3 text-sm">
                <Meta icon={Clock} label="Allow" value={t.days} />
                <Meta icon={MapPin} label="Distance" value={t.from} />
                <Meta icon={Car} label="Vehicle" value={t.vehicle} />
              </div>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-3xl md:text-4xl font-black tracking-tight">{t.title}</h2>
              <p className="mt-4 text-[#595959] leading-relaxed">{t.intro}</p>
              <ol className="mt-8 space-y-4">
                {t.stops.map(([name, body], i) => (
                  <li key={name} className="flex gap-4">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#BA5A3A] text-white font-black flex items-center justify-center">{i + 1}</div>
                    <div><div className="font-black">{name}</div><p className="text-sm text-[#595959] mt-1 leading-relaxed">{body}</p></div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 card-hard p-5 text-sm"><span className="overline text-[#BA5A3A] block mb-1">Good to know</span>{t.tips}</div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/vehicles/hilux" className="btn-ochre" data-testid={`trip-${t.slug}-hilux`}>Book the Hilux <ArrowUpRight className="w-4 h-4"/></Link>
                <Link to="/vehicles/kluger" className="btn-ghost" data-testid={`trip-${t.slug}-kluger`}>Book the Kluger</Link>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3 items-start">
      <Icon className="w-4 h-4 text-[#BA5A3A] mt-0.5 flex-shrink-0" />
      <div><span className="overline text-[#595959]">{label}</span><div className="text-[#1C1C1C]">{value}</div></div>
    </div>
  );
}
